import {
  MISC_TASK_STATUS,
  MiscTaskHistoryEntry,
  MiscTaskPersonName,
  MiscTaskQueue,
  MiscTaskRow,
  MiscTaskViewer,
} from '../../../interfaces/MiscTaskApprovalInterface';

/**
 * NPM Reds — pure presentation + derivation logic for the approval page.
 *
 * Deliberately free of React and of anything that reaches api/client: jest in
 * this repo has no transformIgnorePatterns for axios's ESM build, so a test that
 * touches the client cannot run at all. Everything here is testable in
 * miscTaskHelpers.test.ts; the page itself is not.
 */

// ── Buckets ──────────────────────────────────────────────────────────────────

export interface BucketDef {
  queue: MiscTaskQueue;
  /** The approvalStatus a row in this bucket carries. */
  status: number;
  label: string;
  /** CSS custom-property reference for the bucket's colour. */
  color: string;
}

export const BUCKETS: BucketDef[] = [
  { queue: 'firstLevel', status: MISC_TASK_STATUS.firstPending, label: '1st approval', color: 'var(--s-pending)' },
  { queue: 'secondLevel', status: MISC_TASK_STATUS.secondPending, label: '2nd approval', color: 'var(--accent)' },
  { queue: 'approved', status: MISC_TASK_STATUS.approved, label: 'Approved', color: 'var(--s-approved)' },
  { queue: 'denied', status: MISC_TASK_STATUS.denied, label: 'Denied', color: 'var(--s-denied)' },
];

export const bucketFor = (queue: MiscTaskQueue): BucketDef =>
  BUCKETS.find((b) => b.queue === queue) ?? BUCKETS[0];

/** Which bucket a row currently belongs in, from its status alone. */
export const queueForStatus = (approvalStatus: number): MiscTaskQueue =>
  (BUCKETS.find((b) => b.status === approvalStatus) ?? BUCKETS[0]).queue;

/** Both pending buckets share a column layout and an action column. */
export const isPendingQueue = (queue: MiscTaskQueue): boolean =>
  queue === 'firstLevel' || queue === 'secondLevel';

/**
 * Fixed column widths per bucket. `table-layout: fixed` + a <colgroup> is
 * load-bearing here, not cosmetic: the expanded detail row is a single colspan
 * cell, and under `table-layout: auto` its min-content width is redistributed
 * into the real columns and pushes them off screen.
 */
export const COLUMN_WIDTHS: Record<MiscTaskQueue, string[]> = {
  // The 4th column is SUBMITTED. It carries "Aug 8 · 2:14p" on one nowrap line, so
  // it is 2 points wider than the bare "Aug 8" the ENTRY column used to hold; the
  // 2 points come off the widest neighbour in each layout, keeping every row at 100%.
  firstLevel: ['34px', '13%', '17%', '10%', '7%', '20%', '13%', '20%'],
  secondLevel: ['34px', '13%', '17%', '10%', '7%', '20%', '13%', '20%'],
  approved: ['34px', '12%', '15%', '9%', '7%', '16%', '20%', '12%', '9%'],
  denied: ['34px', '14%', '18%', '10%', '8%', '20%', '16%', '14%'],
};

export const columnCount = (queue: MiscTaskQueue): number => COLUMN_WIDTHS[queue].length;

// ── Formatting ───────────────────────────────────────────────────────────────

export const fullName = (name: MiscTaskPersonName | null | undefined): string =>
  (name ? [name.first, name.middle, name.last].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim() : '');

export const money = (value: number | null | undefined): string =>
  `$${Number(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "-07:00" / "+0530" → minutes from UTC. Null for anything unparseable. */
export function offsetMinutes(offset: string | null | undefined): number | null {
  if (!offset) return null;
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec(offset.trim());
  if (!m) return null;
  const mins = Number(m[2]) * 60 + Number(m[3]);
  return m[1] === '-' ? -mins : mins;
}

/**
 * A calendar date — the work date, or the day part of a stamp.
 *
 * Formatted in UTC on purpose. `entryDate` is a SQL date the API serialises as
 * midnight UTC; rendering it in the viewer's zone moves it to the previous day
 * for anyone west of Greenwich, which is every NPM user.
 */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const src = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso;
  const ms = Date.parse(src);
  if (Number.isNaN(ms)) return String(iso);
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * A full audit stamp — "Sat, Aug 8 · 2:31 PM" — in the ACTOR's wall clock when
 * the trail carries the offset it was recorded with, falling back to the raw
 * instant. Same reasoning as the timesheet exceptions page: without the offset
 * the same approval reads as two different times to two different people.
 */
export function formatStamp(iso: string | null | undefined, offset?: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return String(iso);

  const mins = offsetMinutes(offset);
  // With an offset, shift the instant and format as UTC so the viewer's own
  // timezone cannot move it a second time. Without one, the browser's zone is
  // the only thing left to use.
  const zone = mins == null ? undefined : 'UTC';
  const when = mins == null ? new Date(ms) : new Date(ms + mins * 60000);

  const day = when.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: zone,
  });
  const time = when.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: zone,
  });
  return `${day} · ${time}`;
}

// ── The submit stamp ─────────────────────────────────────────────────────────
// `submittedDateTime` arrives from the API ALREADY OFFSET-COMPOSED — the server
// applied SWITCHOFFSET(InsertDateTime, InsertDateTimeOffset) in SQL and stripped
// the zone marker, so 'YYYY-MM-DDTHH:mm:ss' is literally the submitter's wall
// clock. That makes Date.parse the WRONG tool: it would read the string in the
// viewer's browser zone (or, with a Z, in UTC) and shift an evening submission
// onto the next day — the second half of the very bug the server-side compose
// exists to kill. So these read the digits LEXICALLY and never consult a zone.

interface LocalParts { year: number; month: number; day: number; hour: number; minute: number }

/**
 * 'YYYY-MM-DDTHH:mm(:ss)' or the same with a space. Null for anything else —
 * INCLUDING anything that carries a zone marker, which by definition is not a
 * naked local stamp and must be handed to the instant-aware formatters instead of
 * having its digits read as if they were local.
 */
function localParts(value: string | null | undefined): LocalParts | null {
  if (!value) return null;
  const text = value.trim();
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(text)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(text);
  if (!m) return null;
  return {
    year: Number(m[1]), month: Number(m[2]), day: Number(m[3]),
    hour: Number(m[4]), minute: Number(m[5]),
  };
}

/** The parts as a UTC instant, purely so Intl can name the month and weekday. */
const asUtc = (p: LocalParts): Date =>
  new Date(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute));

/**
 * The submitted DATE — "Aug 8". Falls back to `formatDay` for a value that is not
 * a naked local stamp, so an older API build degrades rather than blanking.
 */
export function formatSubmittedDay(value: string | null | undefined): string {
  const p = localParts(value);
  if (!p) return formatDay(value);
  return asUtc(p).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * The compact time that rides beside the date in the column — "2:14p" / "11:05a".
 * Deliberately terser than the detail stamp: the column is 10% wide and the date
 * is what gets scanned, so the time is present without competing for the line.
 */
export function formatSubmittedTime(value: string | null | undefined): string {
  const p = localParts(value);
  if (!p) return '';
  const suffix = p.hour < 12 ? 'a' : 'p';
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${hour12}:${String(p.minute).padStart(2, '0')}${suffix}`;
}

/**
 * The exact submit time, spelled out for the expanded detail row —
 * "Sat, Aug 8 · 2:14 PM".
 *
 * NO ZONE LABEL. The digits are ALREADY the submitter's own wall clock (the server
 * composed SWITCHOFFSET(InsertDateTime, InsertDateTimeOffset) and stripped the
 * marker), so this is local time and always was. The old " (UTC-07:00)" suffix was
 * a label describing which offset had been applied — but readers took it to mean
 * the time itself was UTC, which is the opposite of what it said. An unlabelled
 * local stamp is read correctly by everyone, and NPM is single-zone anyway.
 *
 * `offset` is still taken because the fallback below needs it: a value carrying a
 * zone marker is not a naked local stamp and must go to the instant-aware
 * formatter, which does need the offset to land in the actor's wall clock.
 */
export function formatSubmittedStamp(
  value: string | null | undefined,
  offset?: string | null
): string {
  const p = localParts(value);
  if (!p) return formatStamp(value, offset);
  const when = asUtc(p);
  const day = when.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
  const time = when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  return `${day} · ${time}`;
}

export interface Ageing {
  hours: number;
  text: string;
  /** Two days is the point at which a red is holding up a timesheet. */
  hot: boolean;
}

/** "18h waiting" / "2d 16h waiting". Null when the red is no longer waiting. */
export function ageing(pendingSince: string | null | undefined, now: Date): Ageing | null {
  if (!pendingSince) return null;
  const ms = Date.parse(pendingSince);
  if (Number.isNaN(ms)) return null;
  const hours = Math.max(0, Math.floor((now.getTime() - ms) / 3600000));
  const text = hours < 24 ? `${hours}h waiting` : `${Math.floor(hours / 24)}d ${hours % 24}h waiting`;
  return { hours, text, hot: hours >= 48 };
}

// ── Capabilities ─────────────────────────────────────────────────────────────
// Mirrors of the server's rules. `actionable` is the one the client must NOT
// re-derive: it already accounts for routing, slots and the caller's identity.

/** The red is this caller's to approve right now. */
export const isMine = (row: MiscTaskRow): boolean =>
  row.actionable && row.approvalStatus < MISC_TASK_STATUS.approved;

export const canDeny = (row: MiscTaskRow, viewer: MiscTaskViewer | null): boolean =>
  row.approvalStatus !== MISC_TASK_STATUS.denied && (!!viewer?.canOversee || isMine(row));

export const canForce = (row: MiscTaskRow, viewer: MiscTaskViewer | null): boolean =>
  !!viewer?.canForceApprove && row.approvalStatus < MISC_TASK_STATUS.approved;

export const canEditDescription = (row: MiscTaskRow, viewer: MiscTaskViewer | null): boolean =>
  !!viewer?.canOversee && row.approvalStatus === MISC_TASK_STATUS.approved;

/** Routing resolved to nobody, so nothing but a force can clear it. */
export const hasNoApprover = (row: MiscTaskRow): boolean =>
  row.approvalStatus < MISC_TASK_STATUS.approved && row.pendingApprovers.length === 0;

// ── Search + sort ────────────────────────────────────────────────────────────

/** Employee name, alt ID, project name and number, phase ID, both descriptions. */
export function rowHaystack(row: MiscTaskRow): string {
  return [
    fullName(row.name),
    row.altEmployeeID,
    row.project?.name,
    row.project?.altProjectID,
    row.altIntermediateID,
    row.intermediateName,
    row.miscDescription,
    row.superDescription,
  ].filter(Boolean).join(' ').toLowerCase();
}

export function rowMatches(row: MiscTaskRow, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return rowHaystack(row).includes(needle);
}

/**
 * Oldest-pending first, because the queue is worked from the top. The resolved
 * buckets have no `pendingSince`, so they fall back to entry date descending —
 * most recently worked first.
 */
export function sortRows(rows: MiscTaskRow[], queue: MiscTaskQueue): MiscTaskRow[] {
  const sorted = [...rows];
  if (isPendingQueue(queue)) {
    sorted.sort((a, b) => {
      const av = Date.parse(a.pendingSince ?? '') || 0;
      const bv = Date.parse(b.pendingSince ?? '') || 0;
      return av - bv || a.miscEmployeeTaskID - b.miscEmployeeTaskID;
    });
  } else {
    sorted.sort((a, b) => {
      const av = Date.parse(a.entryDate ?? '') || 0;
      const bv = Date.parse(b.entryDate ?? '') || 0;
      return bv - av || b.miscEmployeeTaskID - a.miscEmployeeTaskID;
    });
  }
  return sorted;
}

// ── History derivation ───────────────────────────────────────────────────────
// The trail is a flat list; the row carries only the CURRENT generation. Chain
// grouping and the "(was …)" comparisons are both reconstructed here rather than
// asked of the API.

export interface DerivedHistoryEntry extends MiscTaskHistoryEntry {
  /** Which approval chain this entry belongs to. */
  generation: number;
  /** The description this entry replaced, for a DESCRIPTION_EDIT. */
  previousSuperDescription: string | null;
}

/**
 * Walk the newest-first trail assigning chain numbers.
 *
 * `generation` on the row is 1 + the number of REOPENs, and a REOPEN is the
 * first event of the chain it opens. So the newest entry carries the row's
 * generation, and every REOPEN we pass drops the counter for everything older.
 */
export function withGenerations(
  history: MiscTaskHistoryEntry[],
  rowGeneration: number
): DerivedHistoryEntry[] {
  let generation = Math.max(1, rowGeneration || 1);
  const out: DerivedHistoryEntry[] = [];

  history.forEach((entry) => {
    out.push({ ...entry, generation, previousSuperDescription: null });
    if (entry.action === 'REOPEN') generation = Math.max(1, generation - 1);
  });

  // A correction's "(was …)" is the newest snapshot older than it — which, in a
  // newest-first list, is the next entry along that carries one.
  out.forEach((entry, i) => {
    if (entry.action !== 'DESCRIPTION_EDIT') return;
    for (let j = i + 1; j < out.length; j += 1) {
      if (out[j].superDescriptionSnapshot) {
        entry.previousSuperDescription = out[j].superDescriptionSnapshot;
        return;
      }
    }
  });

  return out;
}

/** Newest chain first, each with its entries in newest-first order. */
export function groupByGeneration(entries: DerivedHistoryEntry[]): Array<{
  generation: number;
  entries: DerivedHistoryEntry[];
}> {
  const seen: number[] = [];
  entries.forEach((e) => { if (!seen.includes(e.generation)) seen.push(e.generation); });
  seen.sort((a, b) => b - a);
  return seen.map((generation) => ({
    generation,
    entries: entries.filter((e) => e.generation === generation),
  }));
}

/**
 * The Vista text a reopen wiped — the newest snapshot from a superseded chain.
 * Shown in the reopened note so the approver can see what it used to read.
 */
export function supersededDescription(
  entries: DerivedHistoryEntry[],
  rowGeneration: number
): string | null {
  const older = entries.find(
    (e) => e.generation < rowGeneration && !!e.superDescriptionSnapshot
  );
  return older?.superDescriptionSnapshot ?? null;
}

/** True once someone has corrected the description after the 2nd approval. */
export const wasCorrectedAfterApproval = (entries: MiscTaskHistoryEntry[]): boolean =>
  entries.some((e) => e.action === 'DESCRIPTION_EDIT');

/** The 1st- and 2nd-level approvals that put an approved red where it is. */
export function approvalsByLevel(entries: MiscTaskHistoryEntry[]): {
  first: MiscTaskHistoryEntry | null;
  second: MiscTaskHistoryEntry | null;
} {
  const isApproval = (e: MiscTaskHistoryEntry) =>
    e.action === 'APPROVE' || e.action === 'FORCE_APPROVE';
  return {
    first: entries.find((e) => isApproval(e) && e.approvalLevel === 1) ?? null,
    second: entries.find((e) => isApproval(e) && e.approvalLevel === 2) ?? null,
  };
}

// ── Copy ─────────────────────────────────────────────────────────────────────

/** Who is still owed an approval, as a sentence fragment. */
export function approverNames(row: MiscTaskRow): string {
  return row.pendingApprovers
    .map((p) => p.approvalGroupName || p.name || '')
    .filter(Boolean)
    .join(' and ');
}

/**
 * The second line of the "Waiting on" cell. Two individuals both have to sign;
 * a group needs any one member.
 */
export function waitingNote(row: MiscTaskRow): string | null {
  if (row.pendingApprovers.length > 1) return 'both must approve';
  if (row.pendingApprovers[0]?.approvalGroupName) return 'any member may approve';
  return null;
}

export interface EmptyStateCopy {
  heading: string;
  body: string;
}

/**
 * Bucket empty states. An approver is told about their own queue; oversight and
 * payroll, who never hold a slot, are told about the slice.
 */
export function emptyStateCopy(queue: MiscTaskQueue, canOversee: boolean): EmptyStateCopy {
  switch (queue) {
    case 'firstLevel':
      return {
        heading: canOversee ? 'Nothing waiting on a 1st approval' : 'Nothing needs your 1st approval',
        body: 'New reds land here the day your crew enters them.',
      };
    case 'secondLevel':
      return {
        heading: canOversee ? 'Nothing waiting on a 2nd approval' : 'Nothing needs your 2nd approval',
        body: 'Reds arrive here once their 1st approver signs off.',
      };
    case 'approved':
      return {
        heading: canOversee ? 'Nothing approved yet this period' : 'You have not approved anything yet this period',
        body: 'Approved reds stay here until the pay period closes and they export to Vista.',
      };
    default:
      return {
        heading: canOversee ? 'Nothing denied this period' : 'You have not denied anything this period',
        body: 'Denied reds stay listed with their reason, so nothing disappears.',
      };
  }
}

/**
 * The stage-card sub-line. The big number and this line answer two different
 * questions on purpose: what is on YOU, and what exists in the period.
 */
export function stageSubline(
  queue: MiscTaskQueue,
  periodCount: number,
  canOversee: boolean
): { lead: string; leadEmphasis: boolean; tail: string } {
  const pending = isPendingQueue(queue);
  if (canOversee) {
    return {
      lead: 'in your slice',
      leadEmphasis: false,
      tail: pending ? ' · none waiting on you' : ' · nothing for you to do',
    };
  }
  if (pending) {
    return { lead: 'waiting on you', leadEmphasis: true, tail: ` · ${periodCount} in the period` };
  }
  return {
    lead: queue === 'approved' ? 'you approved' : 'you denied',
    leadEmphasis: false,
    tail: ` · ${periodCount} in the period`,
  };
}

/**
 * The inline message on a rejected action. A rejection means somebody else got
 * there first, so it names the state the red actually landed in rather than
 * repeating that the click failed.
 */
export function rejectionMessage(
  status: number | null,
  serverMessage: string,
  landedIn: MiscTaskQueue | null,
  level: 1 | 2 | null
): { headline: string; body: string } {
  const headline = `Rejected · ${status ?? 'error'}.`;
  const where = landedIn ? ` It is in the ${bucketFor(landedIn).label} bucket now.` : '';
  const lost = level === 2 ? ' Your text was not saved.' : '';
  return { headline, body: `${serverMessage}${where}${lost}` };
}
